import { PayloadAction, createSlice, createSelector } from '@reduxjs/toolkit';

import { ClipPayload } from 'services/clip.service';
import { StoryConfig } from 'services/story.service';
import { FileConfig, FileType } from 'services/file.service';
import { PaymentPrice, PaymentType, SubscriptionType } from 'services/payment.service';
import { FinalCutVersion, getFinalCut, RenderStatus, updateItCanShare } from 'services/finalcut.service';
import { RootState } from 'store/store';
import { createSafeAsyncThunk } from 'store/store.helper';
import { getClipIdFromString, parseClipId, sortClips } from 'components/app/_helpers/_app.helper';
import {
  getRangeMarkerTimes,
  getMarkersEnabledRange,
} from 'components/app/_mediator/range-markers/range-markers.helper';
import { Cells, ClipIdParsed, LinkedClips } from 'components/app/editor/_mediator/editor.slice';
import { markersConversion } from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';

const SLICE_ID = 'fc__data';

export type FcClipRates = Record<ClipId, number> | null;
export type FcClip = { clipId: ClipId } & ClipIdParsed & ClipPayload;
export type ClipsMap = Record<ClipId, FcClip>;

type FcErrorClips = Record<ClipId, string>;
type FcErrorData = { clipId: ClipId; message: string };

export interface SetActiveClip {
  clipId: CellClip;
  cell: CursorCell;
}

interface UpdFcName {
  name: string;
  url: Url;
}

export type FcRangeTime = {
  startTime: Fraction;
  endTime: Fraction;
};

export type FcFileConfigsMap = Record<FileId, FileConfig>;

interface State {
  id: number | null;
  url: Url | null;
  name: string | null;
  fcClipsMap: ClipsMap;
  fcMarkers: MarkerId[];
  fcStoryBoard: CellClip[];
  fcStoryConfig: StoryConfig | null;
  fcLinkedClips: LinkedClips;
  version: FinalCutVersion | null;
  fcStatus: RenderStatus | null;
  fcCells: Cells | null;
  fcErrorClips: FcErrorClips;
  fcRangeTime: FcRangeTime | null;
  fcClipRates: FcClipRates;
  fcFileConfigsMap: FcFileConfigsMap;
  isCanShare: boolean;
}

const initialState: State = {
  id: null,
  name: '',
  url: null,
  fcClipsMap: {},
  fcMarkers: [],
  fcStoryBoard: [],
  fcStoryConfig: null,
  fcLinkedClips: {},
  version: null,
  fcStatus: null,
  fcCells: null,
  fcErrorClips: {},
  fcRangeTime: null,
  fcClipRates: null,
  fcFileConfigsMap: {},
  isCanShare: false,
};

// T H U N K S

export const fetchFinalCut = createSafeAsyncThunk(
  SLICE_ID + '/fetchFinalCut',
  async (finalCutId: FinalcutId, { getState }) => {
    const response = await getFinalCut(finalCutId);

    const { selectedFinalcut } = (getState() as RootState).projects;
    if (response.data.finalCut.id !== selectedFinalcut) return Promise.reject();

    const {
      clips,
      finalCut,
      beatMarkerArray,
      storyBoardConfig: fcStoryBoard,
      finalCutConfig: fcStoryConfig,
      finalCutVersions,
      mediaFiles,
    } = response.data;

    const fcClipsMap: ClipsMap = {};
    const fcLinkedClips: LinkedClips = {};
    const fcFileConfigsMap: FcFileConfigsMap = {};
    const fcMarkers = markersConversion(
      'fromBE',
      beatMarkerArray,
      fcStoryConfig.transitionTime,
      fcStoryConfig.clipTransitionAlignment
    );
    let fcErrorClip: FcErrorClips = {};

    sortClips(
      Object.entries(clips).map(([clipId, data]) => {
        return { clipId, ...parseClipId(clipId), ...data };
      })
    ).forEach((clip) => {
      fcClipsMap[clip.clipId] = clip;
      if (clip.clipGroupId) {
        if (fcLinkedClips[clip.clipGroupId]) fcLinkedClips[clip.clipGroupId].push(clip.clipId);
        else fcLinkedClips[clip.clipGroupId] = [clip.clipId];
      }
    });

    if (finalCut.errorMessageForUser) {
      const clipId = getClipIdFromString(finalCut.errorMessageForUser);
      if (clipId) fcErrorClip = { [clipId]: finalCut.errorMessageForUser };
    }

    mediaFiles.forEach((file) => {
      if (file.fileType === FileType.video) fcFileConfigsMap[file.id] = file.config;
    });

    const version = finalCutVersions[finalCutVersions.length - 1];

    return {
      id: finalCut.id,
      name: finalCut.name,
      url: version.url ?? null,
      status: version.status,
      fcClipsMap,
      fcMarkers,
      fcErrorClip,
      fcStoryBoard,
      fcStoryConfig,
      fcLinkedClips,
      fcFileConfigsMap,
      version,
      isCanShare: finalCut.itCanShare,
    };
  }
);

export const updFcUrls = createSafeAsyncThunk(SLICE_ID + '/updFcUrls', async (fcId: FinalcutId) => {
  const response = await getFinalCut(fcId);
  const { finalCutVersions, clips } = response.data;
  const version = finalCutVersions[finalCutVersions.length - 1];
  const thumbs = Object.entries(clips).map(([clipId, data]) => ({
    clipId,
    url: data.thumbnailUrl,
  }));
  return { fcId, thumbs, url: version.url ?? null };
});

export const allowToShare = createSafeAsyncThunk(SLICE_ID + '/allowToShare', async (finalcutId: FinalcutId) => {
  await updateItCanShare(finalcutId);
});

// S E L E C T O R S

export const fcClipsInRange = createSelector(
  (state: RootState) => state.fc__data.fcStoryBoard,
  (state: RootState) => state.fc__data.fcStoryConfig,
  (fcStoryboard, fcStoryConfig) => {
    return fcStoryboard.map((clip, index) => {
      if (!fcStoryConfig) return null;
      const outOfRange = !getMarkersEnabledRange(fcStoryConfig, fcStoryboard.length ?? 0, 'clip').includes(index);
      if (outOfRange) return null;
      return clip;
    });
  }
);

export const fcStripePlans = createSelector(
  (state: RootState) => state.fc__data.version,
  (state: RootState) => state.fc__data.fcStoryConfig?.resolution,
  (state: RootState) => state.app__data.pricing.productPrices,
  (fcVersion, resolution, pricing) => {
    if (!fcVersion || !pricing.length) return [];
    const paymentType = ((fcVersion: FinalCutVersion) => {
      if (fcVersion.payment) return fcVersion.payment.type;
      switch (fcVersion.subscription!.type) {
        case SubscriptionType.Hd:
          return PaymentType.Hd;
        case SubscriptionType.Fhd:
          return PaymentType.Fhd;
        case SubscriptionType.Uhd:
        case SubscriptionType.Pro:
          return PaymentType.Uhd;
      }
    })(fcVersion);
    if (paymentType === undefined) return [];
    const paidAmount = pricing.find((plan) => plan.type === paymentType)?.amount ?? 0;
    const getPrice = (plan: PaymentPrice, paidAmount: number) => {
      return plan.amount - paidAmount < 0 ? 0 : plan.amount - paidAmount;
    };
    return pricing
      .filter((plan) => plan.type > (resolution ?? paymentType))
      .map((plan) => ({
        value: plan.type,
        label: `${plan.description} $${getPrice(plan, paidAmount)}`,
        price: getPrice(plan, paidAmount),
        description: plan.description,
      }));
  }
);

// S L I C E

export const slice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setFcCells: (state, action: PayloadAction<Cells>) => {
      state.fcCells = action.payload;
    },
    setFcRangeTime: (state, action: PayloadAction<Duration>) => {
      const { fcMarkers, fcStoryConfig } = state;
      const { startMarkerId, finishMarkerId } = fcStoryConfig!;
      const duration = action.payload;
      const { startTime, endTime } = getRangeMarkerTimes({
        startMarkerId,
        finishMarkerId,
        markers: fcMarkers,
        type: 'fraction',
        duration,
      });
      state.fcRangeTime = {
        startTime,
        endTime,
      };
    },
    setFcClipRates: (state, action: PayloadAction<FcClipRates>) => {
      state.fcClipRates = action.payload;
    },
    setFcErrorClips: (state, action: PayloadAction<FcErrorData>) => {
      const { clipId, message } = action.payload;
      state.fcErrorClips[clipId] = message;
    },
    updFcName: (state, action: PayloadAction<UpdFcName>) => {
      state.name = action.payload.name;
      state.url = action.payload.url;
    },
    resetFinalcutData: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFinalCut.fulfilled, (state, action) => {
      state.id = action.payload.id;
      state.url = action.payload.url;
      state.name = action.payload.name;
      state.fcStatus = action.payload.status;
      state.fcMarkers = action.payload.fcMarkers;
      state.fcClipsMap = action.payload.fcClipsMap;
      state.fcStoryBoard = action.payload.fcStoryBoard;
      state.fcStoryConfig = action.payload.fcStoryConfig;
      state.fcFileConfigsMap = action.payload.fcFileConfigsMap;
      state.fcLinkedClips = action.payload.fcLinkedClips;
      state.fcErrorClips = action.payload.fcErrorClip;
      state.version = action.payload.version;
      state.isCanShare = action.payload.isCanShare;
    });
    builder.addCase(updFcUrls.fulfilled, (state, action) => {
      const { fcId, thumbs, url } = action.payload;
      if (state.id !== fcId) return;
      state.url = url;
      thumbs.forEach((el) => {
        if (state.fcClipsMap[el.clipId]) {
          state.fcClipsMap[el.clipId].thumbnailUrl = el.url;
        }
      });
    });
    builder.addCase(allowToShare.fulfilled, (state) => {
      state.isCanShare = true;
    });
  },
});

export const { setFcCells, resetFinalcutData, setFcRangeTime, setFcClipRates, setFcErrorClips, updFcName } =
  slice.actions;
export default slice.reducer;
